import { useEffect, useMemo, useState } from 'react';
import Header from '../components/Header';
import { fetchMonthlyReport } from '../services/api';
import { buildMonthlyReport, formatMonth, getReportMonths } from '../utils/monthlyReport';
import { downloadReport } from '../utils/monthlyReportExport';

const SUBMITTED_BY_KEY = 'monthlyReport.submittedBy';
const LIST_STYLES = ['list-disc', 'list-[circle]', 'list-[square]'];

const readSubmittedBy = () => {
  try {
    return localStorage.getItem(SUBMITTED_BY_KEY) || '';
  } catch (_) {
    return '';
  }
};

const Runs = ({ runs }) =>
  runs.map((r, i) => (r.bold ? <strong key={i}>{r.text}</strong> : <span key={i}>{r.text}</span>));

const ReportList = ({ items, level = 0 }) => (
  <ul className={`${LIST_STYLES[level % LIST_STYLES.length]} pl-8 space-y-1 mt-1`}>
    {items.map((item, i) => (
      <li key={i}>
        <Runs runs={item.runs} />
        {item.children && <ReportList items={item.children} level={level + 1} />}
      </li>
    ))}
  </ul>
);

const ReportTable = ({ block }) => {
  const totalWidth = block.widths.reduce((a, w) => a + w, 0);
  return (
    <div className="overflow-x-auto mt-2">
      <table
        className={`border-collapse ${block.small ? 'text-sm' : ''}`}
        style={{ width: `${totalWidth * 100}%`, minWidth: block.small ? 640 : undefined }}
      >
        {block.header && (
          <thead>
            <tr>
              {block.header.map((cell, i) => (
                <th key={i} className="border border-gray-800 bg-gray-100 px-2 py-1 text-left font-bold">{cell}</th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {block.rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} className="border border-gray-800 px-2 py-1 break-words" style={{ width: `${(block.widths[j] / totalWidth) * 100}%` }}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ReportBlock = ({ block }) => {
  switch (block.type) {
    case 'title':
      return <h2 className="text-center font-bold text-lg">{block.text}</h2>;
    case 'subtitle':
      return <p className="text-center font-bold">{block.text}</p>;
    case 'paragraph':
      return <p className="mt-8"><Runs runs={block.runs} /></p>;
    case 'list':
      return <ReportList items={block.items} />;
    case 'table':
      return <ReportTable block={block} />;
    default:
      return null;
  }
};

const MonthlyReport = () => {
  const months = useMemo(() => getReportMonths(), []);
  // Default to the most recent complete month
  const [month, setMonth] = useState(months[1]?.value ?? months[0].value);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submittedBy, setSubmittedBy] = useState(readSubmittedBy);
  const [downloading, setDownloading] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setData(null);
        const report = await fetchMonthlyReport(month);
        if (!cancelled) setData(report);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load report');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [month]);

  const blocks = useMemo(() => (data ? buildMonthlyReport(data, { submittedBy }) : null), [data, submittedBy]);

  const handleSubmittedByChange = (value) => {
    setSubmittedBy(value);
    try {
      localStorage.setItem(SUBMITTED_BY_KEY, value);
    } catch (_) {
      // no-op if localStorage unavailable
    }
  };

  const handleDownload = async (format) => {
    setDownloading(format);
    setDownloadError(null);
    try {
      await downloadReport(blocks, format, `WtW Data Report ${formatMonth(month)}.${format}`);
    } catch (err) {
      console.error('Error exporting monthly report:', err);
      setDownloadError(`Could not create the ${format === 'docx' ? 'Word' : 'PDF'} file.`);
    } finally {
      setDownloading(null);
    }
  };

  const buttonClass = 'px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header pageTitle="Monthly Data Report" />

      <div className="px-4 md:px-10 py-6">
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <label htmlFor="monthSelect" className="font-semibold">Month:</label>
          <select
            id="monthSelect"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="px-3 py-1.5 rounded-md border border-gray-300 bg-white"
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>

          <label htmlFor="submittedBy" className="font-semibold ml-0 md:ml-4">Submitted by:</label>
          <input
            id="submittedBy"
            type="text"
            value={submittedBy}
            onChange={(e) => handleSubmittedByChange(e.target.value)}
            placeholder="Your name (optional)"
            className="px-3 py-1.5 rounded-md border border-gray-300 bg-white"
          />

          <div className="flex gap-3 md:ml-auto">
            <button onClick={() => handleDownload('docx')} disabled={!blocks || !!downloading} className={buttonClass}>
              {downloading === 'docx' ? 'Preparing…' : 'Download Word'}
            </button>
            <button onClick={() => handleDownload('pdf')} disabled={!blocks || !!downloading} className={buttonClass}>
              {downloading === 'pdf' ? 'Preparing…' : 'Download PDF'}
            </button>
          </div>
        </div>

        {downloadError && <div className="mb-4 text-red-600">{downloadError}</div>}

        <div className="max-w-[8.5in] mx-auto bg-white shadow rounded-sm px-6 py-8 md:px-16 md:py-14 font-serif text-gray-900 leading-relaxed">
          {loading ? (
            <div className="flex justify-center items-center h-64 text-lg text-gray-600 font-sans">
              Loading {formatMonth(month)} report… this can take a few seconds.
            </div>
          ) : error ? (
            <div className="flex justify-center items-center h-64 text-lg text-red-600 font-sans">Error: {error}</div>
          ) : (
            blocks.map((block, i) => <ReportBlock key={i} block={block} />)
          )}
        </div>
      </div>
    </div>
  );
};

export default MonthlyReport;
